import { type FC, useRef, useState } from "react";

import { faPause } from "@fortawesome/pro-duotone-svg-icons/faPause";
import { faPlay } from "@fortawesome/pro-duotone-svg-icons/faPlay";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { formatClock } from "@application/utilities/format_clock";

// A quiet pill player: play/pause, a seek bar, and the clock. `duration`
// (when known) fills the clock before the audio's own metadata arrives.
export const AudioPlayer: FC<{ src: string; duration?: number | null }> = ({
  src,
  duration: knownDuration,
}) => {
  const audio = useRef<HTMLAudioElement>(null);
  const [playing, setPlaying] = useState(false);
  const [time, setTime] = useState(0);
  const [duration, setDuration] = useState(knownDuration ?? 0);
  const filled = duration > 0 ? (time / duration) * 100 : 0;

  const toggle = () => {
    if (!audio.current) return;
    if (audio.current.paused) void audio.current.play();
    else audio.current.pause();
  };

  return (
    <div className="flex h-11 min-w-[280px] flex-1 items-center gap-3 rounded-full bg-white/75 pr-4 pl-2 shadow-[inset_0_1px_0_#fff] ring-1 ring-indigo-500/15">
      <audio
        ref={audio}
        src={src}
        preload="metadata"
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onTimeUpdate={(event) => setTime(event.currentTarget.currentTime)}
        onLoadedMetadata={(event) => {
          // Streams can report Infinity; keep the known length then.
          const { duration } = event.currentTarget;
          if (Number.isFinite(duration)) setDuration(duration);
        }}
      />
      <button
        type="button"
        aria-label={playing ? "Pause" : "Play"}
        onClick={toggle}
        className="inline-flex size-[30px] shrink-0 items-center justify-center rounded-full bg-linear-to-b from-indigo-600 to-indigo-700 text-[11px] text-white shadow-[inset_0_1px_0_rgba(255,255,255,.3),0_6px_14px_-6px_color-mix(in_srgb,var(--color-indigo-600)_80%,transparent)]"
      >
        <FontAwesomeIcon icon={playing ? faPause : faPlay} />
      </button>
      <input
        type="range"
        aria-label="Position"
        min={0}
        max={duration || 0}
        step={0.1}
        value={time}
        onChange={(event) => {
          if (audio.current)
            audio.current.currentTime = Number(event.target.value);
        }}
        style={{
          background: `linear-gradient(to right, var(--color-indigo-500) ${filled}%, var(--color-hairline) ${filled}%)`,
        }}
        className="h-1 flex-1 cursor-pointer appearance-none rounded-sm [&::-moz-range-thumb]:size-3 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:border-0 [&::-moz-range-thumb]:bg-indigo-600 [&::-webkit-slider-thumb]:size-3 [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-indigo-600"
      />
      <span className="text-xs text-slate-500 tabular-nums">
        {formatClock(time)} / {formatClock(duration)}
      </span>
    </div>
  );
};
